import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Box,
    Typography,
    InputAdornment,
    Alert
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

const DEFAULT_TIERS = [
    { tier_number: 1, max_usage_therms: 250, delivery_rate_per_therm: 0, commodity_rate_per_therm: 0 },
    { tier_number: 2, max_usage_therms: 4167, delivery_rate_per_therm: 0, commodity_rate_per_therm: 0 },
    { tier_number: 3, max_usage_therms: null, delivery_rate_per_therm: 0, commodity_rate_per_therm: 0 }
];

export default function GasTariffBuilder({ open, onSave, onCancel }) {
    const [tariffName, setTariffName] = useState('');
    const [utilityName, setUtilityName] = useState('');
    const [rateSchedule, setRateSchedule] = useState('');
    const [customerCharge, setCustomerCharge] = useState(0);
    const [tiers, setTiers] = useState(DEFAULT_TIERS);
    const [error, setError] = useState('');

    const handleAddTier = () => {
        const newTierNumber = tiers.length + 1;
        setTiers([
            ...tiers,
            { tier_number: newTierNumber, max_usage_therms: null, delivery_rate_per_therm: 0, commodity_rate_per_therm: 0 }
        ]);
    };

    const handleRemoveTier = (index) => {
        if (tiers.length <= 1) {
            setError('Must have at least one tier');
            return;
        }

        const newTiers = tiers.filter((_, i) => i !== index);
        // Re-number tiers
        newTiers.forEach((tier, i) => {
            tier.tier_number = i + 1;
        });
        setTiers(newTiers);
    };

    const handleTierChange = (index, field, value) => {
        const newTiers = [...tiers];
        if (field === 'max_usage_therms') {
            newTiers[index][field] = value === '' || value === null ? null : parseFloat(value);
        } else {
            // For rate fields, default to 0 instead of null
            newTiers[index][field] = value === '' || value === null ? 0 : parseFloat(value);
        }
        setTiers(newTiers);
    };

    const handleSave = () => {
        setError('');

        // Validate required fields
        if (!tariffName.trim()) {
            setError('Tariff name is required');
            return;
        }

        // Validate tiers
        for (const tier of tiers) {
            if (tier.delivery_rate_per_therm < 0) {
                setError(`Tier ${tier.tier_number}: Delivery rate cannot be negative`);
                return;
            }
            if (tier.commodity_rate_per_therm < 0) {
                setError(`Tier ${tier.tier_number}: Commodity rate cannot be negative`);
                return;
            }
        }

        const tariffData = {
            name: tariffName,
            utility_name: utilityName,
            rate_schedule_name: rateSchedule,
            tariff_type: 'natural_gas',
            rate_data: {
                customer_charge_per_day: parseFloat(customerCharge) || 0,
                tiers: tiers.map(tier => ({
                    tier_number: tier.tier_number,
                    max_usage_therms: tier.max_usage_therms,
                    delivery_rate_per_therm: tier.delivery_rate_per_therm,
                    commodity_rate_per_therm: tier.commodity_rate_per_therm,
                    rate_per_therm: tier.delivery_rate_per_therm + tier.commodity_rate_per_therm  // backwards compat
                }))
            }
        };

        onSave(tariffData);
        handleClose();
    };

    const handleClose = () => {
        // Reset form
        setTariffName('');
        setUtilityName('');
        setRateSchedule('');
        setCustomerCharge(0);
        setTiers(DEFAULT_TIERS);
        setError('');
        onCancel();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>Create Natural Gas Tariff</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        label="Tariff Name"
                        value={tariffName}
                        onChange={(e) => setTariffName(e.target.value)}
                        required
                        fullWidth
                        placeholder="e.g., SoCal Gas G-10"
                    />

                    <TextField
                        label="Utility Name"
                        value={utilityName}
                        onChange={(e) => setUtilityName(e.target.value)}
                        fullWidth
                        placeholder="e.g., Southern California Gas"
                    />

                    <TextField
                        label="Rate Schedule"
                        value={rateSchedule}
                        onChange={(e) => setRateSchedule(e.target.value)}
                        fullWidth
                        placeholder="e.g., G-10"
                    />

                    <TextField
                        label="Fixed Customer Charge"
                        type="number"
                        value={customerCharge || ''}
                        onChange={(e) => setCustomerCharge(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            endAdornment: <InputAdornment position="end">/day</InputAdornment>
                        }}
                        fullWidth
                    />

                    <Typography variant="subtitle1" sx={{ mt: 2 }}>
                        Usage Tiers
                    </Typography>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Tier</TableCell>
                                <TableCell>Up To (therms/month)</TableCell>
                                <TableCell>Delivery ($/therm)</TableCell>
                                <TableCell>Commodity ($/therm)</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tiers.map((tier, index) => (
                                <TableRow key={index}>
                                    <TableCell>Tier {tier.tier_number}</TableCell>
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            value={tier.max_usage_therms || ''}
                                            onChange={(e) => handleTierChange(index, 'max_usage_therms', e.target.value)}
                                            placeholder="Unlimited"
                                            size="small"
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            value={tier.delivery_rate_per_therm || ''}
                                            onChange={(e) => handleTierChange(index, 'delivery_rate_per_therm', e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            value={tier.commodity_rate_per_therm || ''}
                                            onChange={(e) => handleTierChange(index, 'commodity_rate_per_therm', e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        ${((tier.delivery_rate_per_therm || 0) + (tier.commodity_rate_per_therm || 0)).toFixed(4)}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            onClick={() => handleRemoveTier(index)}
                                            size="small"
                                            color="error"
                                            aria-label="Delete tier"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddTier}
                        variant="outlined"
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        Add Tier
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    Save Tariff
                </Button>
            </DialogActions>
        </Dialog>
    );
}
